/**
 * Affiliate Partners Management Page
 * Allows brands to invite creators as affiliate partners and manage partnerships
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

interface Creator {
  id: string;
  store_name: string;
  store_description: string;
  user_id: string;
  profile_image?: string;
  banner_image?: string;
  bio?: string;
  social_links?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
  };
  total_videos?: number;
  total_followers?: number;
  avg_engagement_rate?: number;
}

interface Partnership {
  id: string;
  brand_id: string;
  creator_id: string;
  status: 'pending' | 'active' | 'inactive' | 'rejected';
  invited_at: string;
  accepted_at: string | null;
  rejected_at: string | null;
  notes: string | null;
  creator: Creator;
}

export default function AffiliatePartners() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Creator[]>([]);
  const [searching, setSearching] = useState(false);
  const [inviteNotes, setInviteNotes] = useState('');
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [creatorInfoDialogOpen, setCreatorInfoDialogOpen] = useState(false);
  const [viewingCreator, setViewingCreator] = useState<Creator | null>(null);

  useEffect(() => {
    loadBrandProfile();
  }, [user]);

  useEffect(() => {
    if (brandId) {
      loadPartnerships();
    }
  }, [brandId]);

  const loadBrandProfile = async () => {
    if (!user) {
      setError('로그인이 필요합니다.');
      setLoading(false);
      return;
    }

    try{

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Profile error:', profileError);
        setError('프로필을 불러올 수 없습니다: ' + profileError.message);
        setLoading(false);
        return;
      }

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', profile.id)
        .single();

      if (sellerError) {
        console.error('Seller profile error:', sellerError);
        setError('셀러 프로필을 찾을 수 없습니다. 셀러 등록이 필요합니다.');
        setLoading(false);
        return;
      }

      if (!sellerProfile) {
        setError('셀러 프로필이 존재하지 않습니다. 셀러 등록을 먼저 진행해주세요.');
        setLoading(false);
        return;
      }

      setBrandId(sellerProfile.id);
    } catch (err: any) {
      console.error('Load brand profile error:', err);
      setError('브랜드 프로필을 불러올 수 없습니다: ' + err.message);
      setLoading(false);
    }
  };

  const loadPartnerships = async () => {
    try {
      setLoading(true);

      // First, get partnerships
      const { data: partnershipsData, error: partnershipsError } = await supabase
        .from('affiliate_partnerships')
        .select('*')
        .eq('brand_id', brandId)
        .order('invited_at', { ascending: false });

      if (partnershipsError) throw partnershipsError;

      if (!partnershipsData || partnershipsData.length === 0) {
        setPartnerships([]);
        return;
      }

      // Get unique creator IDs
      const creatorIds = Array.from(new Set(partnershipsData.map((p) => p.creator_id)));

      // Fetch creator info from seller_profile_public view
      const { data: creatorsData, error: creatorsError } = await supabase
        .from('seller_profile_public')
        .select('id, store_name, store_description, profile_image, banner_image, bio, social_links, total_videos, total_followers, avg_engagement_rate, user_id')
        .in('id', creatorIds);

      if (creatorsError) {
        console.error('Error fetching creators:', creatorsError);
        // Continue with null creators rather than failing completely
      }

      // Map creators by ID for quick lookup
      const creatorsMap = new Map(
        (creatorsData || []).map((c) => [c.id, c])
      );

      // Combine partnerships with creator info
      const combinedData = partnershipsData.map((partnership) => ({
        ...partnership,
        creator: creatorsMap.get(partnership.creator_id) || {
          id: partnership.creator_id,
          store_name: '알 수 없음',
          store_description: '',
          user_id: '',
        },
      }));

      setPartnerships(combinedData);
    } catch (err: any) {
      setError('파트너십 목록을 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchCreators = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      // Use seller_profile_public view instead of seller_profile table
      // This view only exposes non-sensitive fields (id, store_name, store_description, etc.)
      const { data, error } = await supabase
        .from('seller_profile_public')
        .select('id, store_name, store_description, user_id, profile_image, banner_image, bio, social_links, total_videos, total_followers, avg_engagement_rate')
        .ilike('store_name', `%${searchQuery}%`)
        .neq('id', brandId) // Exclude self
        .limit(10);

      if (error) throw error;

      // Filter out already invited creators
      const existingCreatorIds = partnerships.map((p) => p.creator_id);
      const filtered = (data || []).filter((c) => !existingCreatorIds.includes(c.id));

      console.log('🔍 검색 디버깅:', {
        searchQuery,
        totalResults: data?.length || 0,
        alreadyInvited: existingCreatorIds.length,
        filteredResults: filtered.length,
        rawData: data,
        existingIds: existingCreatorIds,
        filtered
      });

      setSearchResults(filtered);
    } catch (err: any) {
      setError('크리에이터 검색 실패: ' + err.message);
    } finally {
      setSearching(false);
    }
  };

  const inviteCreator = async () => {
    if (!selectedCreator || !brandId) return;
    if (!user) {
      setError('Not authenticated');
      return;
    }

    try {

      const { data: profile } = await supabase.from('profiles').select('id').eq('id', user.id).single();

      const { error } = await supabase.from('affiliate_partnerships').insert({
        brand_id: brandId,
        creator_id: selectedCreator.id,
        status: 'pending',
        invited_by: profile?.id,
        notes: inviteNotes || null,
      });

      if (error) throw error;

      setSuccess('파트너 초대를 보냈습니다!');
      setOpenInviteDialog(false);
      setSelectedCreator(null);
      setInviteNotes('');
      setSearchQuery('');
      setSearchResults([]);
      loadPartnerships();
    } catch (err: any) {
      setError('초대 실패: ' + err.message);
    }
  };

  const updatePartnershipStatus = async (partnershipId: string, newStatus: 'active' | 'inactive') => {
    try {
      const updateData: any = { status: newStatus };

      if (newStatus === 'inactive') {
        updateData.deactivated_at = new Date().toISOString();
      }

      const { error } = await supabase.from('affiliate_partnerships').update(updateData).eq('id', partnershipId);

      if (error) throw error;

      setSuccess(`파트너십이 ${newStatus === 'active' ? '활성화' : '비활성화'}되었습니다.`);
      loadPartnerships();
    } catch (err: any) {
      setError('상태 변경 실패: ' + err.message);
    }
  };

  const handleViewCreatorInfo = (creator: Creator) => {
    setViewingCreator(creator);
    setCreatorInfoDialogOpen(true);
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      pending: { label: '대기중', color: 'warning' as const },
      active: { label: '활성', color: 'success' as const },
      inactive: { label: '비활성', color: 'default' as const },
      rejected: { label: '거절됨', color: 'error' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const filteredPartnerships = partnerships.filter((p) => {
    if (tabValue === 0) return p.status === 'active';
    if (tabValue === 1) return p.status === 'pending';
    if (tabValue === 2) return p.status === 'inactive';
    if (tabValue === 3) return p.status === 'rejected';
    return true;
  });

  return (
    <Box sx={styles.pageContainer}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={styles.title}>
          어필리에이트 파트너 관리
        </Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenInviteDialog(true)}>
          파트너 초대
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ ...styles.paper, mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`활성 (${partnerships.filter((p) => p.status === 'active').length})`} />
          <Tab label={`대기중 (${partnerships.filter((p) => p.status === 'pending').length})`} />
          <Tab label={`비활성 (${partnerships.filter((p) => p.status === 'inactive').length})`} />
          <Tab label={`거절됨 (${partnerships.filter((p) => p.status === 'rejected').length})`} />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ ...styles.paper, ...styles.tableContainer }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>크리에이터</TableCell>
                <TableCell>설명</TableCell>
                <TableCell>상태</TableCell>
                <TableCell>초대일</TableCell>
                <TableCell>수락일</TableCell>
                <TableCell align="right">액션</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPartnerships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" sx={styles.text}>파트너가 없습니다</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPartnerships.map((partnership) => (
                  <TableRow key={partnership.id}>
                    <TableCell>
                      <Typography fontWeight="bold" sx={styles.text}>{partnership.creator.store_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ ...styles.text, maxWidth: 300 }}>
                        {partnership.creator.store_description}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(partnership.status)}</TableCell>
                    <TableCell>{new Date(partnership.invited_at).toLocaleDateString()}</TableCell>
                    <TableCell>{partnership.accepted_at ? new Date(partnership.accepted_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="크리에이터 정보 보기">
                        <IconButton size="small" color="primary" onClick={() => handleViewCreatorInfo(partnership.creator)}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      {partnership.status === 'active' && (
                        <Tooltip title="비활성화">
                          <IconButton size="small" onClick={() => updatePartnershipStatus(partnership.id, 'inactive')}>
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {partnership.status === 'inactive' && (
                        <Tooltip title="활성화">
                          <IconButton size="small" color="success" onClick={() => updatePartnershipStatus(partnership.id, 'active')}>
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Invite Dialog */}
      <Dialog open={openInviteDialog} onClose={() => setOpenInviteDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: styles.paper }}>
        <DialogTitle sx={styles.text}>크리에이터 초대</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="크리에이터 검색"
            placeholder="스토어 이름 또는 설명으로 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') searchCreators();
            }}
            sx={{ ...styles.input, mt: 2, mb: 2 }}
          />
          <Button variant="outlined" onClick={searchCreators} disabled={searching} fullWidth>
            {searching ? '검색 중...' : '검색'}
          </Button>

          {searchResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ ...styles.text, mb: 1 }}>
                검색 결과
              </Typography>
              {searchResults.map((creator) => (
                <Paper
                  key={creator.id}
                  sx={{
                    ...styles.paper,
                    p: 2,
                    mb: 1,
                    cursor: 'pointer',
                    border: selectedCreator?.id === creator.id ? '2px solid' : '1px solid',
                    borderColor: selectedCreator?.id === creator.id ? 'primary.main' : 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => setSelectedCreator(creator)}
                >
                  <Typography fontWeight="bold" sx={styles.text}>{creator.store_name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    {creator.store_description}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}

          {selectedCreator && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="초대 메시지 (선택사항)"
              placeholder="파트너십에 대한 메모나 조건을 입력하세요"
              value={inviteNotes}
              onChange={(e) => setInviteNotes(e.target.value)}
              sx={{ ...styles.input, mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInviteDialog(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={inviteCreator} disabled={!selectedCreator}>
            초대 보내기
          </Button>
        </DialogActions>
      </Dialog>

      {/* Creator Info Dialog */}
      <Dialog
        open={creatorInfoDialogOpen}
        onClose={() => setCreatorInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: styles.paper }}
      >
        <DialogTitle sx={styles.text}>크리에이터 프로필</DialogTitle>
        <DialogContent>
          {viewingCreator && (
            <Box>
              {/* Banner Image */}
              {viewingCreator.banner_image && (
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    backgroundImage: `url(${viewingCreator.banner_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
              )}

              {/* Profile Image and Name */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                {viewingCreator.profile_image ? (
                  <Box
                    component="img"
                    src={viewingCreator.profile_image}
                    alt={viewingCreator.store_name}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? '#333' : '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h4" sx={{ color: isDarkMode ? '#fff' : '#666' }}>
                      {viewingCreator.store_name.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" fontWeight="bold" sx={styles.text}>
                    {viewingCreator.store_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    {viewingCreator.store_description}
                  </Typography>
                </Box>
              </Box>

              {/* Bio */}
              {viewingCreator.bio && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ...styles.text, mb: 1 }}>
                    소개
                  </Typography>
                  <Typography variant="body2" sx={styles.text}>
                    {viewingCreator.bio}
                  </Typography>
                </Box>
              )}

              {/* Stats */}
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    비디오
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={styles.text}>
                    {viewingCreator.total_videos?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    팔로워
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={styles.text}>
                    {viewingCreator.total_followers?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    평균 참여율
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={styles.text}>
                    {viewingCreator.avg_engagement_rate ? `${viewingCreator.avg_engagement_rate.toFixed(2)}%` : '0%'}
                  </Typography>
                </Box>
              </Box>

              {/* Social Links */}
              {viewingCreator.social_links && Object.keys(viewingCreator.social_links).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ...styles.text, mb: 1 }}>
                    소셜 링크
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {viewingCreator.social_links.instagram && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<InstagramIcon />}
                        href={viewingCreator.social_links.instagram}
                        target="_blank"
                        sx={{
                          borderColor: isDarkMode ? '#fff' : '#000',
                          color: isDarkMode ? '#fff' : '#000',
                        }}
                      >
                        Instagram
                      </Button>
                    )}
                    {viewingCreator.social_links.youtube && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<YouTubeIcon />}
                        href={viewingCreator.social_links.youtube}
                        target="_blank"
                        sx={{
                          borderColor: isDarkMode ? '#fff' : '#000',
                          color: isDarkMode ? '#fff' : '#000',
                        }}
                      >
                        YouTube
                      </Button>
                    )}
                    {viewingCreator.social_links.tiktok && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LanguageIcon />}
                        href={viewingCreator.social_links.tiktok}
                        target="_blank"
                        sx={{
                          borderColor: isDarkMode ? '#fff' : '#000',
                          color: isDarkMode ? '#fff' : '#000',
                        }}
                      >
                        TikTok
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatorInfoDialogOpen(false)}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
